import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, User } from '../types';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { handleApiError } from '../utils/errorHandler';
import { logger } from '../utils/logger';

interface AuthStore extends AuthState {
  refreshToken: string | null;
  // Impersonation
  isImpersonating: boolean;
  originalToken: string | null;
  originalUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) => Promise<void>;
  verifyToken: () => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  getRefreshToken: () => string | null;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}

const API_URL = API_BASE_URL ? `${API_BASE_URL}/api` : '/api';

// Debug: afficher l'URL utilisée dans le store
logger.log('🔗 AuthStore API_URL:', API_URL);

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isImpersonating: false,
      originalToken: null,
      originalUser: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          logger.log('🔐 Tentative de connexion avec:', email);

          // Appel à l'API réelle
          const response = await axios.post(`${API_URL}/auth/login`, {
            email,
            password
          });

          const { token, refreshToken, user } = response.data;

          logger.log('📝 Mise à jour du store avec:', { user: user.email, token: token.substring(0, 20) + '...' });

          set({
            user,
            token,
            refreshToken: refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
          });

          logger.log('✅ Connexion réussie:', user.email);
        } catch (error: any) {
          handleApiError(error, 'la connexion');
          set({ isLoading: false });
          throw new Error(error.response?.data?.message || 'Erreur de connexion');
        }
      },

      register: async (userData) => {
        set({ isLoading: true });

        try {
          logger.log('📝 Tentative d\'inscription:', userData.email);

          // Appel à l'API réelle
          const response = await axios.post(`${API_URL}/auth/register`, userData);

          const { token, refreshToken, user } = response.data;

          set({
            user,
            token,
            refreshToken: refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
          });

          logger.log('✅ Inscription réussie:', user.email);
        } catch (error: any) {
          handleApiError(error, 'l\'inscription');
          set({ isLoading: false });
          throw new Error(error.response?.data?.message || 'Erreur d\'inscription');
        }
      },

      logout: () => {
        logger.log('🚪 Déconnexion déclenchée');
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isImpersonating: false,
          originalToken: null,
          originalUser: null,
        });
        delete axios.defaults.headers.common['Authorization'];
        logger.log('✅ Déconnexion terminée');
      },

      verifyToken: async () => {
        const { token, user } = get();
        logger.log('🔍 Vérification du token:', { hasToken: !!token, hasUser: !!user, userEmail: user?.email });

        if (!token || !user) {
          logger.log('⚠️ Pas de token ou utilisateur, déconnexion');
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          return;
        }

        try {
          // Vérifier le token auprès de l'API
          const response = await axios.get(`${API_URL}/auth/verify`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          logger.log('✅ Token vérifié pour l\'utilisateur:', user.email);

          // Mettre à jour l'utilisateur si nécessaire
          if (response.data.user) {
            set({ user: response.data.user });
          }
        } catch (error: any) {
          // Ne déconnecter QUE sur 401 (token invalide/expiré)
          // Pas sur erreur réseau, timeout, 500, etc.
          if (error.response?.status === 401) {
            logger.log('🚫 Token invalide (401), déconnexion');
            set({
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
            });
            delete axios.defaults.headers.common['Authorization'];
          } else {
            logger.log('⚠️ Erreur réseau/serveur lors de la vérification, on garde la session:', error.message);
          }
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      setToken: (token: string) => {
        set({ token });
      },

      getRefreshToken: () => {
        return get().refreshToken;
      },

      impersonate: async (userId: string) => {
        const { token, user } = get();
        try {
          const response = await axios.post(
            `${API_URL}/auth/impersonate/${userId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const { token: clientToken, user: clientUser } = response.data;
          set({
            originalToken: token,
            originalUser: user,
            token: clientToken,
            user: clientUser,
            isImpersonating: true,
            isAuthenticated: true,
          });
        } catch (error: any) {
          handleApiError(error, "l'impersonation");
          throw error;
        }
      },

      exitImpersonation: () => {
        const { originalToken, originalUser } = get();
        if (!originalToken || !originalUser) return;
        set({
          token: originalToken,
          user: originalUser,
          isImpersonating: false,
          originalToken: null,
          originalUser: null,
          isAuthenticated: true,
        });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
